import { useNavigate } from 'react-router-dom';
import type { EventListItem } from '../../api/types';

// 권위: docs/ARCH_DASHBOARD.md §4.4 "연관 이벤트 체인 (이 신호 → 이 주문 → 이 체결)".
// trace_id 로 묶인 이벤트를 timestamp ASC 로 타임라인으로 그린다.
// - 본 컴포넌트는 표현에만 집중 — 데이터는 useTraceChain 훅이 공급.
// - 같은 event 자체 (currentEventId) 는 하이라이트.
// - 클릭 시 해당 이벤트 상세로 딥링크 (/history/:eventId).

interface Props {
  traceId: string | null;
  items: EventListItem[];
  isLoading: boolean;
  currentEventId?: string;
}

export function AgentEventTimeline({
  traceId,
  items,
  isLoading,
  currentEventId,
}: Props): JSX.Element {
  const navigate = useNavigate();

  if (!traceId) {
    return (
      <div className="agent-hist-timeline__empty">
        trace_id 가 없는 이벤트입니다. 체인을 표시할 수 없습니다.
      </div>
    );
  }
  if (isLoading) {
    return <div className="agent-hist-timeline__empty">체인 불러오는 중…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="agent-hist-timeline__empty">
        같은 trace_id 로 묶인 다른 이벤트가 없습니다.
      </div>
    );
  }

  // timestamp ASC — 흐름 읽기 쉽게.
  const sorted = [...items].sort(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime()
  );

  return (
    <ol className="agent-hist-timeline" aria-label="연관 이벤트 체인">
      {sorted.map((it) => {
        const sev = it.severity.toLowerCase();
        const isCurrent = it.eventId === currentEventId;
        return (
          <li
            key={it.eventId}
            className={`agent-hist-timeline__item agent-hist-timeline__item--${sev}${
              isCurrent ? ' agent-hist-timeline__item--current' : ''
            }`}
          >
            <button
              type="button"
              className="agent-hist-timeline__link"
              onClick={() => navigate(`/history/${it.eventId}`)}
              disabled={isCurrent}
            >
              <span className="agent-hist-timeline__time">{formatTs(it.occurredAt)}</span>
              <span className={`syslog-badge syslog-badge--${sev}`}>{it.severity}</span>
              <code className="agent-hist-timeline__code">{it.eventCode}</code>
              <span className="agent-hist-timeline__summary">{it.summary}</span>
              {isCurrent && <span className="agent-hist-timeline__marker">현재</span>}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
